import type { ReactNode } from 'react';
import { ALIGN_CLASS, headingAlign, headingLabel, type Heading } from './headings';

/**
 * The multi-line document editor every purchase, old gold receipt and exchange
 * shares.
 *
 * Lines are added and removed but never silently reordered — a shop's staff
 * read a document by its line numbers, and a row that moves while they are
 * looking at it is how the wrong piece gets weighed.
 */
export function LineTable({
  head,
  children,
  onAdd,
  addLabel,
  footer,
}: {
  head: Heading[];
  children: ReactNode;
  onAdd: () => void;
  addLabel: string;
  footer?: ReactNode;
}) {
  return (
    // A jewellery line can carry a dozen figures, so the table scrolls sideways
    // rather than squeezing every column until nothing can be read or typed.
    <div className="overflow-x-auto rounded-lg border border-ink/10 bg-white">
      <table className="w-full min-w-[60rem]">
        <thead className="bg-gold-50">
          <tr>
            <th className="th w-10">#</th>
            {head.map((h, i) => (
              <th key={i} className={`th whitespace-nowrap ${ALIGN_CLASS[headingAlign(h)]}`}>
                {headingLabel(h)}
              </th>
            ))}
            <th className="th w-10" />
          </tr>
        </thead>
        <tbody>{children}</tbody>
        {footer}
      </table>

      <div className="border-t border-ink/10 p-2">
        <button type="button" className="btn-ghost py-1 text-sm" onClick={onAdd}>
          + {addLabel}
        </button>
      </div>
    </div>
  );
}

export function LineRow({
  index,
  onRemove,
  removable,
  children,
}: {
  index: number;
  onRemove: () => void;
  removable: boolean;
  children: ReactNode;
}) {
  return (
    <tr className="align-top">
      <td className="td num text-ink-soft">{index + 1}</td>
      {children}
      <td className="td">
        {removable ? (
          <button type="button" className="text-xs text-danger underline" onClick={onRemove}>
            Remove
          </button>
        ) : null}
      </td>
    </tr>
  );
}

/** A compact cell input, sized for a table rather than a form. */
export function CellInput({
  value,
  onChange,
  placeholder,
  numeric,
  decimals,
  max,
  maxLength,
  width = 'w-28',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  numeric?: boolean;
  /** Money takes two places, weight three, fine weight four. */
  decimals?: number;
  max?: number;
  maxLength?: number;
  width?: string;
}) {
  const clean = (raw: string): string => {
    if (!numeric) return maxLength ? raw.slice(0, maxLength) : raw;
    const digits = raw.replace(/[^\d.]/g, '');
    const [whole = '', ...rest] = digits.split('.');
    let next = rest.length === 0 ? whole : `${whole}.${rest.join('').slice(0, decimals ?? 6)}`;
    if (max !== undefined && next !== '' && Number(next) > max) next = String(max);
    return next;
  };

  const box = (
    <input
      // Hints are pale so an empty box never reads as a figure already typed.
      className={`field ${width} px-2 py-1 text-sm placeholder:text-silver-400 ${numeric ? 'num text-right' : ''}`}
      inputMode={numeric ? 'decimal' : undefined}
      // Chrome reads a row of unlabelled number boxes as a form it recognises
      // and offers to "save your vehicle". These are weights and rupees; the
      // browser has no business remembering them.
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      data-lpignore="true"
      data-form-type="other"
      data-1p-ignore="true"
      value={value}
      maxLength={maxLength}
      onChange={(e) => onChange(clean(e.target.value))}
      placeholder={placeholder}
    />
  );

  // A figure's heading is right-aligned, so the box under it has to sit right
  // as well. Left in the cell, the heading floated over empty space and the
  // column read as though it belonged to the column beside it.
  return numeric ? <div className="flex justify-end">{box}</div> : box;
}

export function CellSelect({
  value,
  onChange,
  options,
  placeholder,
  width = 'w-32',
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  width?: string;
}) {
  return (
    <select className={`field ${width} px-2 py-1 text-sm`} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder ?? '—'}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
